import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';
import type { UserProfile } from '../types/profile';

export function useProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) {
        setProfile(null);
        setIsAdmin(false);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // First ensure profile exists
        const { data: profileData, error: profileError } = await supabase
          .rpc('ensure_user_profile', {
            user_id: user.id
          });

        if (profileError) throw profileError;

        // Check admin status
        const { data: adminData } = await supabase
          .from('admin_users')
          .select('email')
          .eq('email', user.email)
          .maybeSingle();

        setProfile(profileData);
        setIsAdmin(!!adminData);

      } catch (err) {
        console.error('Error fetching profile:', err);
        setError(err instanceof Error ? err : new Error('Failed to fetch profile'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();

    // Subscribe to profile changes
    const subscription = supabase
      .channel('profile_changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'user_profiles',
        filter: `id=eq.${user?.id}`
      }, (payload) => {
        if (payload.new) {
          setProfile(payload.new as UserProfile);
        }
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [user]);

  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user) throw new Error('Must be logged in to update profile');

    try {
      setError(null);
      const { data, error: updateError } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('id', user.id)
        .select()
        .single();

      if (updateError) throw updateError;
      if (data) setProfile(data);

      return data;
    } catch (err) {
      console.error('Error updating profile:', err);
      throw err instanceof Error ? err : new Error('Failed to update profile');
    }
  };

  return { 
    profile,
    isAdmin,
    isLoading, 
    error,
    updateProfile
  };
}